import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasInlineError,
  ArtifactCanvasSaveActions,
  Button,
  Label,
  Separator,
  Skeleton,
  Switch,
} from '@yeshuman/sdk'
import { Check, Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import {
  type ImproveChip,
  type ImprovePreview,
  type ImproveTarget,
  useImproveApply,
  useImprovePreview,
} from '../../hooks/use-improve'
import { ImproveChangeRow } from './improve-change-row'
import { ImproveMissingList, formatStrength } from './improve-missing-list'

const COPY = {
  profile: {
    checking: 'Checking your profile...',
    chipsHint: 'Skills from the TalentCo lexicon that your profile text already mentions.',
    noRepair: 'Automatic repair is off here, so only skill suggestions are shown.',
    saved: 'Profile updated. Your matches will refresh.',
  },
  opportunity: {
    checking: 'Checking this job...',
    chipsHint: 'Skills from the TalentCo lexicon that the job description already mentions.',
    noRepair: 'Automatic repair is off here, so only skill suggestions are shown.',
    saved: 'Job updated. Matches for this job will refresh.',
  },
} as const

type ChipChoice = Record<string, 'required' | 'preferred'>

function SkillChipPicker({
  chips,
  selected,
  withRequirement,
  disabled,
  onToggle,
  onRequirementChange,
}: {
  chips: ImproveChip[]
  selected: ChipChoice
  withRequirement: boolean
  disabled: boolean
  onToggle: (chip: ImproveChip) => void
  onRequirementChange: (chip: ImproveChip, required: boolean) => void
}) {
  const picked = chips.filter((chip) => selected[chip.id])
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {chips.map((chip) => {
          const on = Boolean(selected[chip.id])
          return (
            <Button
              key={chip.id}
              type="button"
              size="xs"
              variant={on ? 'secondary' : 'outline'}
              aria-pressed={on}
              title={chip.target ? `Adds to ${chip.target}` : undefined}
              disabled={disabled}
              onClick={() => onToggle(chip)}
            >
              {on ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Plus className="h-3.5 w-3.5" aria-hidden />}
              {chip.name}
            </Button>
          )
        })}
      </div>
      {withRequirement && picked.length ? (
        <ul className="divide-y divide-border/60 rounded-md border border-border/60">
          {picked.map((chip) => {
            const switchId = `improve-chip-required-${chip.id}`
            return (
              <li key={chip.id} className="flex items-center justify-between gap-4 px-3 py-2">
                <span className="text-sm">{chip.name}</span>
                <span className="flex items-center gap-2">
                  <Label htmlFor={switchId} className="text-xs text-muted-foreground">
                    Required
                  </Label>
                  <Switch
                    id={switchId}
                    checked={selected[chip.id] === 'required'}
                    disabled={disabled}
                    onCheckedChange={(value) => onRequirementChange(chip, value)}
                  />
                </span>
              </li>
            )
          })}
        </ul>
      ) : null}
      {!withRequirement && picked.length ? (
        <p className="text-xs text-muted-foreground">
          {picked.map((chip) => `${chip.name} → ${chip.target ?? 'Profile skills'}`).join(' · ')}
        </p>
      ) : null}
    </div>
  )
}

function PreviewSkeleton({ label }: { label: string }) {
  return (
    <div className="space-y-4" role="status" aria-live="polite">
      <p className="text-sm text-muted-foreground">{label}</p>
      <Skeleton className="h-5 w-1/3" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  )
}

export function ImproveReviewCanvas({ target, onClose }: { target: ImproveTarget; onClose: () => void }) {
  const copy = COPY[target.kind]
  const preview = useImprovePreview(target)
  const apply = useImproveApply(target)
  const [accepted, setAccepted] = useState<Set<string>>(new Set())
  const [chipChoice, setChipChoice] = useState<ChipChoice>({})
  const data: ImprovePreview | undefined = preview.data

  const runPreview = useCallback(() => {
    setAccepted(new Set())
    setChipChoice({})
    apply.reset()
    preview.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    runPreview()
  }, [runPreview])

  const approvedCount = accepted.size + Object.keys(chipChoice).length
  const allChangeIds = useMemo(() => (data?.changes ?? []).map((change) => change.id), [data])
  const busy = preview.isPending || apply.isPending

  const toggleChange = (id: string, on: boolean) =>
    setAccepted((prev) => {
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  const toggleChip = (chip: ImproveChip) =>
    setChipChoice((prev) => {
      const next = { ...prev }
      if (next[chip.id]) delete next[chip.id]
      else next[chip.id] = chip.requirement_type ?? 'required'
      return next
    })

  const handleSave = () => {
    if (!data || approvedCount === 0) return
    apply.mutate(
      {
        token: data.token,
        accepted: [...accepted],
        chips: Object.entries(chipChoice).map(([id, requirement_type]) => ({ id, requirement_type })),
      },
      {
        onSuccess: (result) => {
          toast.success(copy.saved, {
            description: `${result.applied} approved change${result.applied === 1 ? '' : 's'} saved. Strength now ${formatStrength(result.completeness.score)}.`,
          })
          onClose()
        },
      },
    )
  }

  const errorMessage = (preview.error ?? apply.error)?.message ?? null
  const showProjected = data && data.changes.length > 0 && data.after.score > data.before.score

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
        {preview.isPending || (!data && !preview.isError) ? (
          <PreviewSkeleton label={copy.checking} />
        ) : data ? (
          <>
            <section className="space-y-3" aria-labelledby="improve-missing-heading">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 id="improve-missing-heading" className="text-base font-semibold">What's missing</h3>
                <span className="text-sm text-muted-foreground">
                  Strength {formatStrength(data.before.score)}
                  {showProjected ? ` → ${formatStrength(data.after.score)} with every suggestion` : ''}
                </span>
              </div>
              <ImproveMissingList missing={data.before.missing} />
              {!data.repair_available ? <p className="text-xs text-muted-foreground">{copy.noRepair}</p> : null}
            </section>

            {data.chips.length ? (
              <>
                <Separator />
                <section className="space-y-3" aria-labelledby="improve-chips-heading">
                  <div className="space-y-1">
                    <h3 id="improve-chips-heading" className="text-base font-semibold">Suggested skills</h3>
                    <p className="text-sm text-muted-foreground">{copy.chipsHint}</p>
                  </div>
                  <SkillChipPicker
                    chips={data.chips}
                    selected={chipChoice}
                    withRequirement={target.kind === 'opportunity'}
                    disabled={busy}
                    onToggle={toggleChip}
                    onRequirementChange={(chip, required) =>
                      setChipChoice((prev) => ({ ...prev, [chip.id]: required ? 'required' : 'preferred' }))
                    }
                  />
                </section>
              </>
            ) : null}

            {data.changes.length ? (
              <>
                <Separator />
                <section className="space-y-1" aria-labelledby="improve-changes-heading">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 id="improve-changes-heading" className="text-base font-semibold">Suggested changes</h3>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        setAccepted(accepted.size === allChangeIds.length ? new Set() : new Set(allChangeIds))
                      }
                    >
                      {accepted.size === allChangeIds.length ? 'Clear all' : 'Select all'}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Review each change. Only the ones you tick are saved.
                  </p>
                  <ul className="divide-y divide-border/60">
                    {data.changes.map((change) => (
                      <ImproveChangeRow
                        key={change.id}
                        change={change}
                        checked={accepted.has(change.id)}
                        disabled={busy}
                        onCheckedChange={(on) => toggleChange(change.id, on)}
                      />
                    ))}
                  </ul>
                </section>
              </>
            ) : null}

            {!data.changes.length && !data.chips.length ? (
              <p className="text-sm text-muted-foreground">No suggestions right now. Fill the missing sections directly.</p>
            ) : null}
          </>
        ) : null}
      </div>

      <ArtifactCanvasInlineError>
        {errorMessage ? (
          <span className="flex flex-wrap items-center gap-2">
            {errorMessage}
            <Button type="button" variant="link" size="sm" onClick={runPreview}>
              Check again
            </Button>
          </span>
        ) : null}
      </ArtifactCanvasInlineError>

      <ArtifactCanvasFooterBar
        footerClassName="shrink-0"
        leading={
          data ? (
            <span className="text-xs text-muted-foreground">
              {approvedCount ? `${approvedCount} approved` : 'Nothing is saved until you approve it'}
            </span>
          ) : undefined
        }
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={handleSave}
            isDirty={approvedCount > 0}
            isSaving={apply.isPending}
            saveDisabled={!data || approvedCount === 0 || preview.isPending}
            saveLabel={approvedCount ? `Save ${approvedCount} approved` : 'Save approved'}
            savingLabel="Saving..."
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
